import { useMemo, useState } from 'react';
import { useAdminAuditLog } from '../useAdmin';
import type { AdminAuditFilters, AdminAuditRow } from '../adminApi';
import './AdminAuditLog.css';

const PAGE_SIZE = 30;

const ACTION_OPTIONS = [
  { value: '', label: 'Any action' },
  { value: 'user.role_changed', label: 'Role changed' },
  { value: 'user.suspended', label: 'User suspended' },
  { value: 'user.unsuspended', label: 'User unsuspended' },
  { value: 'user.deleted', label: 'User deleted' },
  { value: 'classroom.paused', label: 'Classroom paused' },
  { value: 'classroom.unpaused', label: 'Classroom unpaused' },
  { value: 'submission.force_approved', label: 'Submission force-approved' },
  { value: 'submission.force_rejected', label: 'Submission force-rejected' },
  { value: 'publishedapp.takedown', label: 'Published app taken down' },
  { value: 'report.dismissed', label: 'Report dismissed' },
  { value: 'report.actioned', label: 'Report actioned' },
  { value: 'config.changed', label: 'Config changed' },
  { value: 'flag.toggled', label: 'Feature flag toggled' },
  { value: 'digest.sent', label: 'Digest sent' },
];

const TARGET_TYPE_OPTIONS = [
  { value: '', label: 'Any target' },
  { value: 'user', label: 'Users' },
  { value: 'classroom', label: 'Classrooms' },
  { value: 'submission', label: 'Submissions' },
  { value: 'published_app', label: 'Published apps' },
  { value: 'report', label: 'Reports' },
  { value: 'config', label: 'Configuration' },
  { value: 'flag', label: 'Feature flags' },
  { value: 'system', label: 'System' },
];

/**
 * Admin activity log (F12). Read-only feed of every mutating action
 * an admin has taken through the console. Filterable by action,
 * target type, target id, and acting admin.
 */
export function AdminAuditLog(): JSX.Element {
  const [action, setAction] = useState('');
  const [targetType, setTargetType] = useState('');
  const [targetId, setTargetId] = useState('');
  const [page, setPage] = useState(0);

  const filters = useMemo<AdminAuditFilters>(
    () => ({
      action: action || undefined,
      target_type: targetType || undefined,
      target_id: targetId.trim() || undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [action, targetType, targetId, page],
  );

  const { data, isLoading, isError, error, isFetching } = useAdminAuditLog(filters);

  const total = data?.total ?? 0;
  const showing = data?.rows.length ?? 0;
  const startIndex = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const endIndex = page * PAGE_SIZE + showing;
  const canPrev = page > 0;
  const canNext = (page + 1) * PAGE_SIZE < total;

  function reset<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      setPage(0);
    };
  }

  return (
    <section className="bl-admin-audit" aria-label="Admin activity log">
      <header className="bl-admin-audit__header">
        <div>
          <h2>Activity log</h2>
          <p className="bl-admin-audit__sub">
            Every change made through the admin console, newest first.
          </p>
        </div>
        <p className="bl-admin-audit__count" aria-live="polite">
          {isLoading
            ? 'Loading…'
            : total === 0
              ? 'No events match'
              : `${startIndex.toLocaleString()}–${endIndex.toLocaleString()} of ${total.toLocaleString()}`}
          {isFetching && !isLoading && ' · refreshing…'}
        </p>
      </header>

      <form className="bl-admin-audit__filters" onSubmit={(e) => e.preventDefault()} role="search">
        <label className="bl-admin-audit__field">
          <span className="bl-admin-audit__field-label">Action</span>
          <select value={action} onChange={(e) => reset(setAction)(e.target.value)}>
            {ACTION_OPTIONS.map((o) => (
              <option key={o.value || 'any'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="bl-admin-audit__field">
          <span className="bl-admin-audit__field-label">Target type</span>
          <select value={targetType} onChange={(e) => reset(setTargetType)(e.target.value)}>
            {TARGET_TYPE_OPTIONS.map((o) => (
              <option key={o.value || 'any'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="bl-admin-audit__field">
          <span className="bl-admin-audit__field-label">Target ID</span>
          <input
            type="text"
            value={targetId}
            onChange={(e) => reset(setTargetId)(e.target.value)}
            placeholder="UUID"
            spellCheck={false}
          />
        </label>
      </form>

      {isError && (
        <p className="bl-admin-audit__error" role="alert">
          Could not load the activity log (
          {error instanceof Error ? error.message : 'unknown error'}).
        </p>
      )}

      <ul className="bl-admin-audit__list">
        {data?.rows.map((row) => (
          <AuditEntry key={row.id} row={row} />
        ))}
        {!isLoading && data?.rows.length === 0 && (
          <li className="bl-admin-audit__empty">No events match the current filters.</li>
        )}
      </ul>

      <nav className="bl-admin-audit__pagination" aria-label="Pagination">
        <button
          type="button"
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          disabled={!canPrev}
        >
          ← Previous
        </button>
        <span className="bl-admin-audit__page-label">
          Page {page + 1}
          {total > 0 && ` of ${Math.max(1, Math.ceil(total / PAGE_SIZE))}`}
        </span>
        <button type="button" onClick={() => setPage((p) => p + 1)} disabled={!canNext}>
          Next →
        </button>
      </nav>
    </section>
  );
}

function AuditEntry({ row }: { row: AdminAuditRow }): JSX.Element {
  return (
    <li className="bl-admin-audit__entry" data-action={row.action}>
      <div className="bl-admin-audit__entry-head">
        <span className="bl-admin-audit__action">{humanAction(row.action)}</span>
        <span className="bl-admin-audit__when" title={row.created_at}>
          {formatRelative(row.created_at)}
        </span>
      </div>
      <p className="bl-admin-audit__line">
        <span className="bl-admin-audit__by">{row.admin_email || '(unknown admin)'}</span>
        {row.target_label && (
          <>
            <span aria-hidden="true"> · </span>
            <span className="bl-admin-audit__target">{row.target_label}</span>
          </>
        )}
      </p>
      {hasPayload(row.payload) && (
        <pre className="bl-admin-audit__payload">{JSON.stringify(row.payload, null, 2)}</pre>
      )}
    </li>
  );
}

function hasPayload(payload: Record<string, unknown>): boolean {
  return Object.keys(payload).length > 0;
}

function humanAction(action: string): string {
  const matched = ACTION_OPTIONS.find((o) => o.value === action);
  return matched ? matched.label : action;
}

function formatRelative(iso: string): string {
  const ts = new Date(iso).getTime();
  if (!Number.isFinite(ts)) return iso;
  const diffMs = Date.now() - ts;
  const mins = Math.round(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}
